import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';

import TakenImages from '../../../models/TakenImages';
import Improvement from '../../../models/helperTypes/Improvement';
import FileUploadState from '../../../models/helperTypes/FileUploadState';

/**
 * Payload for the addCarImage action reducer
 */
export type AddCarImagePayload = {
  imageName: string;
  base64: string;
};

/**
 * Payload for the updateTextValues action reducer
 */
export type TextValuesPayload = {
  previousDamages: string;
  carImprovements: Improvement[];
  vinNumber: string;
  licensePlate: string;
  serialMotor: string;
  type: string;
};

/**
 * Map structure that keeps tab of each Picture's upload state
 */
export type UploadStateMap = {
  [key: string]: FileUploadState;
};

/**
 * This slice's state shape
 */
export type InspectionFormState = {
  // Images
  carImages: TakenImages;
  vehicleRegistryFileName: string;
  vehicleRegistryImage: string;
  carInvoiceBase64: string;
  carInvoiceFilename: string;

  // File's upload state map
  uploadStateMap: UploadStateMap;

  // Text Values
  textValues: TextValuesPayload;

  // Creation status
  inspectionCreationStatus: RequestIndicatorTypes;

  // The unique id returned by the server, once the inspection is created
  createdInspectionUID: string;

  // Since uploading is a multi-step process, this message will notify the user in
  // What step it is currently in
  networkMessage: string;

  blockSearchLicense: boolean;
};

const initialState: InspectionFormState = {
  carImages: {},
  uploadStateMap: {},
  vehicleRegistryFileName: '',
  carInvoiceBase64: '',
  carInvoiceFilename: '',
  vehicleRegistryImage: '',
  inspectionCreationStatus: 'initial',
  textValues: {
    previousDamages: '',
    carImprovements: [],
    vinNumber: '',
    licensePlate: '',
    serialMotor: '',
    type: '',
  },
  createdInspectionUID: '',
  networkMessage: '',
  blockSearchLicense: false,
};

const inspectionFormSlice = createSlice({
  name: 'inspectionForm',
  initialState,
  reducers: {
    overwriteInspectionForm: (
      _,
      action: PayloadAction<InspectionFormState>,
    ): InspectionFormState => {
      return action.payload;
    },
    addCarImage: (state, action: PayloadAction<AddCarImagePayload>): void => {
      state.carImages[action.payload.imageName] = action.payload.base64;
    },
    removeCarImage: (state, action: PayloadAction<string>): void => {
      delete state.carImages[action.payload];
    },
    resetCarImages: (state): void => {
      state.carImages = {};
    },
    updateUploadStateMap: (
      state,
      action: PayloadAction<UploadStateMap>,
    ): void => {
      state.uploadStateMap = { ...state.uploadStateMap, ...action.payload };
    },
    updateVehicleRegistryFileName: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.vehicleRegistryFileName = action.payload;
    },
    updateVehicleRegistryImage: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.vehicleRegistryImage = action.payload;
    },
    updateCarInvoiceBase64: (state, action: PayloadAction<string>): void => {
      state.carInvoiceBase64 = action.payload;
    },
    updateCarInvoiceFilename: (state, action: PayloadAction<string>): void => {
      state.carInvoiceFilename = action.payload;
    },
    updateTextValues: (
      state,
      action: PayloadAction<TextValuesPayload>,
    ): void => {
      state.textValues = action.payload;
    },
    updateBlockSearchLicense: (state, action: PayloadAction<boolean>): void => {
      state.blockSearchLicense = action.payload;
    },
    updateInspectionCreationStatus: (
      state,
      action: PayloadAction<RequestIndicatorTypes>,
    ): void => {
      state.inspectionCreationStatus = action.payload;
    },
    updateInspectionUID: (state, action: PayloadAction<string>): void => {
      state.createdInspectionUID = action.payload;
    },
    updateNetworkMessage: (state, action: PayloadAction<string>): void => {
      state.networkMessage = action.payload;
    },
  },
});

export const {
  overwriteInspectionForm,
  addCarImage,
  removeCarImage,
  resetCarImages,
  updateUploadStateMap,
  updateVehicleRegistryFileName,
  updateVehicleRegistryImage,
  updateInspectionCreationStatus,
  updateTextValues,
  updateInspectionUID,
  updateNetworkMessage,
  updateBlockSearchLicense,
  updateCarInvoiceFilename,
  updateCarInvoiceBase64,
} = inspectionFormSlice.actions;

export default inspectionFormSlice.reducer;
